<template>
  <div class="bg-white rounded-xl shadow-lg p-6">
    <div class="flex items-center justify-between mb-4">
      <h2 class="text-xl font-bold text-gray-900 flex items-center gap-2">
        <span>👥</span>
        Análisis de Vendedores
      </h2>
      <div class="text-sm text-gray-600">
        {{ analisis.total_vendedores }} vendedores
      </div>
    </div>

    <!-- Resumen -->
    <div class="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 mb-6">
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
        <div>
          <p class="text-sm text-gray-600">Mejor Score</p>
          <p class="text-2xl font-bold text-purple-600">{{ analisis.resumen.mejor_vendedor.performance_score.toFixed(1) }}</p>
          <p class="text-xs text-gray-500 truncate">{{ analisis.resumen.mejor_vendedor.vendedor }}</p>
        </div>
        <div>
          <p class="text-sm text-gray-600">Promedio</p>
          <p class="text-2xl font-bold text-blue-600">{{ analisis.resumen.promedio_performance.toFixed(1) }}</p>
        </div>
        <div>
          <p class="text-sm text-gray-600">Excelentes</p>
          <p class="text-2xl font-bold text-green-600">{{ analisis.resumen.vendedores_excelentes }}</p>
        </div>
        <div>
          <p class="text-sm text-gray-600">Críticos</p>
          <p class="text-2xl font-bold text-red-600">{{ analisis.resumen.vendedores_criticos }}</p>
        </div>
      </div>
    </div>

    <!-- Top 5 Ranking -->
    <div v-if="analisis.ranking_top_5 && analisis.ranking_top_5.length > 0" class="mb-6">
      <h3 class="font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <span>🏆</span>
        Top 5 Vendedores
      </h3>
      <div class="space-y-2">
        <div
          v-for="(vendedor, index) in analisis.ranking_top_5"
          :key="vendedor.vendedor_id"
          class="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <div class="text-2xl">{{ getMedalla(index) }}</div>
          <div class="flex-1 min-w-0">
            <p class="font-semibold text-gray-900 truncate">{{ vendedor.vendedor }}</p>
            <div class="flex gap-4 text-xs text-gray-600 mt-1">
              <span>💰 {{ formatearMoneda(vendedor.metricas.valor_ventas) }}</span>
              <span>📦 {{ vendedor.metricas.total_ventas }} ventas</span>
            </div>
          </div>
          <div class="text-right">
            <div class="text-2xl font-bold text-purple-600">{{ vendedor.performance_score.toFixed(0) }}</div>
            <div :class="getClasificacionBadgeClass(vendedor.clasificacion)" class="text-xs px-2 py-0.5 rounded-full mt-1">
              {{ vendedor.clasificacion }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Lista completa (limitada) -->
    <div>
      <h3 class="font-semibold text-gray-900 mb-3">Todos los Vendedores</h3>
      <div class="max-h-96 overflow-y-auto space-y-3">
        <div
          v-for="vendedor in analisis.detalle_vendedores"
          :key="vendedor.vendedor_id"
          class="border rounded-lg p-4 hover:shadow-md transition-all"
        >
          <div class="flex items-start justify-between mb-3">
            <div class="flex-1 min-w-0">
              <h4 class="font-semibold text-gray-900 truncate">{{ vendedor.vendedor }}</h4>
              <p class="text-xs text-gray-500 truncate">{{ vendedor.email }}</p>
            </div>
            <div class="flex items-center gap-2 ml-2">
              <span :class="getClasificacionBadgeClass(vendedor.clasificacion)" class="px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap">
                {{ vendedor.clasificacion }}
              </span>
              <span class="text-xl font-bold text-purple-600">{{ vendedor.performance_score.toFixed(0) }}</span>
            </div>
          </div>

          <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div>
              <p class="text-gray-600">Ventas</p>
              <p class="font-semibold text-gray-900">{{ vendedor.metricas.total_ventas }}</p>
            </div>
            <div>
              <p class="text-gray-600">Valor</p>
              <p class="font-semibold text-green-600">{{ formatearMoneda(vendedor.metricas.valor_ventas) }}</p>
            </div>
            <div>
              <p class="text-gray-600">Producción</p>
              <p class="font-semibold" :class="getTasaColor(vendedor.produccion.tasa_produccion)">
                {{ vendedor.produccion.tasa_produccion.toFixed(0) }}%
              </p>
            </div>
            <div>
              <p class="text-gray-600">Instalación</p>
              <p class="font-semibold" :class="getTasaColor(vendedor.instalacion.tasa_instalacion)">
                {{ vendedor.instalacion.tasa_instalacion.toFixed(0) }}%
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AnalisisVendedores',
  props: {
    analisis: {
      type: Object,
      required: true
    }
  },
  methods: {
    getMedalla(index) {
      const medallas = ['🥇', '🥈', '🥉', '4️⃣', '5️⃣'];
      return medallas[index] || '•';
    },
    formatearMoneda(valor) {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(valor);
    },
    getClasificacionBadgeClass(clasificacion) {
      switch (clasificacion) {
        case 'EXCELENTE':
          return 'bg-green-100 text-green-800';
        case 'BUENO':
          return 'bg-blue-100 text-blue-800';
        case 'REGULAR':
          return 'bg-yellow-100 text-yellow-800';
        case 'BAJO':
          return 'bg-orange-100 text-orange-800';
        case 'CRITICO':
          return 'bg-red-100 text-red-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    },
    getTasaColor(tasa) {
      if (tasa >= 80) return 'text-green-600';
      if (tasa >= 60) return 'text-blue-600';
      if (tasa >= 40) return 'text-yellow-600';
      return 'text-red-600';
    }
  }
};
</script>
